<template>
  <div v-if="data">
    <div class="bg-primary-900 text-white">
      <app-page-wrapper class="grid place-content-center gap-8 pt-8 md:pt-16">
        <h1
          class="balance m-auto max-w-[700px] text-center font-title text-2xl !leading-normal sm:text-3xl md:text-5xl"
        >
          {{ title }}
        </h1>

        <div class="flex justify-center gap-4">
          <template v-if="isCertificateOwner">
            <certificate-add-to-linkedin-button :certificate="data" />

            <app-button
              color="light"
              :href="`/api/certificates/${data.id}/printable`"
              icon="ph:download-simple"
              size="lg"
              title="Download"
              variant="outlined"
              @click="$analytics.track('certificate_download_clicked')"
            />
          </template>

          <app-button
            v-else-if="data.session.course.public"
            color="light"
            size="lg"
            :to="{
              name: 'courses-slug',
              params: { slug: data.session.course.slug },
            }"
          >
            {{ $t("certificate.page.view_course_label") }}
          </app-button>
        </div>
      </app-page-wrapper>
    </div>

    <div
      class="bg-gradient-to-b from-primary-900 from-50% to-primary-50 to-50% pt-16"
    >
      <app-page-wrapper class="!px-4 md:!px-10">
        <certificate-card id="card" :certificate="data" class="m-auto" />
      </app-page-wrapper>
    </div>

    <div class="bg-primary-50 py-32">
      <app-page-wrapper>
        <course-card-list-section id="certificate" :list="courses" />
      </app-page-wrapper>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { CertificateView } from "~~/model/certificate.model";

definePageMeta({
  onboarding: false,
});

const { me } = useCurrentUser();
const { data } = await useFetch<CertificateView>(
  `/api/certificates/${
    useRoute("certificates-certificateId").params.certificateId
  }`,
);

const { t } = useI18n();

if (!data.value)
  throw createError({
    statusCode: 404,
    statusMessage: t("app.errors.page_not_found"),
  });

const isCertificateOwner = computed(() => me.value?.id === data.value?.user_id);
const { $host } = useNuxtApp();

useSeoMeta({
  title: () =>
    t("certificate.page.title", { name: data.value?.user.full_name }),
  ogImage: `${$host}/images/brand/certificate-og-image.png`,
});

const { data: courses } = useCourseList("certificate", {
  limit: 10,
  excludeSlugs: [data.value.session.course.slug],
});

const title = computed(() => {
  if (isCertificateOwner.value) return t("certificate.page.owner_title");

  return t("certificate.page.viewer_title");
});

if (import.meta.client && isCertificateOwner.value) {
  launchConfetti();
}

const { $analytics } = useNuxtApp();
</script>
